import { useState, type DragEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDirectory } from '../api/queries/directory';
import { useLookups, activeOptionsFor, useLookupLabel } from '../api/queries/lookups';
import { ApiError } from '../api/client';
import { can } from '../auth/store';
import { AddCompanyModal } from '../components/AddCompanyModal';
import { AddIndividualModal } from '../components/AddIndividualModal';
import { VcardImportModal } from '../components/VcardImportModal';
import { PersonAvatar } from '../components/PersonAvatar';
import { OrgAvatar } from '../components/OrgAvatar';
import { SortableTh } from '../components/SortableTh';
import { TableScroll } from '../components/TableScroll';
import { useClientSort } from '../hooks/useClientSort';
import type { DirectoryEntry } from '@stfors/shared-types';
import './DirectoryPage.css';

type DirSortKey = 'name' | 'type' | 'roles';

// Where a directory row links: companies → org detail; individuals → their
// most relevant role detail (client preferred, else contractor). Vendor-only
// individuals have no standalone page yet, so they aren't linked.
function entryHref(e: DirectoryEntry): string | null {
  if (e.entry_type === 'company' && e.organization_id !== null) {
    return `/organizations/${e.organization_id}`;
  }
  // Every individual — client, contractor, or vendor — opens its person page,
  // the single editable surface for the shared people row.
  if (e.entry_type === 'individual' && e.person_id !== null) {
    return `/people/${e.person_id}`;
  }
  return null;
}

export function DirectoryPage(): JSX.Element {
  const navigate = useNavigate();
  const [q, setQ] = useState('');
  const [role, setRole] = useState('');
  const [addCompanyOpen, setAddCompanyOpen] = useState(false);
  const [addIndividualOpen, setAddIndividualOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [dropRow, setDropRow] = useState<string | null>(null);
  const { data, isPending, error } = useDirectory({ q, role });
  const roleLookups = useLookups('contact_role');
  const roleLabel = useLookupLabel('contact_role');

  const canWrite = can('organization.write');
  const entries = data?.entries ?? [];
  const sort = useClientSort(entries, (e, key: DirSortKey) => {
    switch (key) {
      case 'name': return e.display_name;
      case 'type': return e.entry_type;
      case 'roles': return e.roles.join(', ');
      default: return '';
    }
  }, { key: 'name', dir: 'asc' });

  // Drop a .vcf onto an individual row → open that person pre-filled from the
  // card (the person page reads the text out of the router state on arrival).
  const onRowDrop = (e: DragEvent<HTMLTableRowElement>, personId: number) => {
    e.preventDefault();
    setDropRow(null);
    const file = Array.from(e.dataTransfer.files).find((f) => /\.vcf$/i.test(f.name) || f.type === 'text/vcard');
    if (file) void file.text().then((txt) => navigate(`/people/${personId}`, { state: { vcardText: txt, vcardName: file.name } }));
  };

  return (
    <section className="section-shell">
      <header className="section-header">
        <h2>Directory</h2>
        <div className="directory-header-actions">
          <span className="directory-count">{entries.length} shown</span>
          {can('organization.write') && (
            <>
              <button type="button" className="btn-cyan-primary" onClick={() => setAddCompanyOpen(true)}>
                + Add Company
              </button>
              <button type="button" className="btn-charcoal-secondary" onClick={() => setAddIndividualOpen(true)}>
                + Add Individual
              </button>
              <button type="button" className="btn-charcoal-secondary" onClick={() => setImportOpen(true)}>
                Import .vcf
              </button>
            </>
          )}
        </div>
      </header>

      <div className="directory-filters">
        <input
          type="search"
          className="form-input"
          placeholder="Search companies and individuals…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
        <select className="form-input" value={role} onChange={(e) => setRole(e.target.value)}>
          <option value="">All roles</option>
          {activeOptionsFor(roleLookups.data?.lookup_options, 'contact_role').map((o) => (
            <option key={o.id} value={o.value}>{o.label ?? o.value}</option>
          ))}
        </select>
      </div>

      {isPending ? (
        <p className="directory-status">Loading directory…</p>
      ) : error ? (
        <p className="directory-status directory-error">
          {error instanceof ApiError && error.status === 403
            ? "You don't have permission to view the directory."
            : 'Failed to load the directory. Try again in a moment.'}
        </p>
      ) : entries.length === 0 ? (
        <div className="section-body">
          <p>No companies or individuals match.</p>
        </div>
      ) : (
        <div className="section-body section-body-flush">
          <TableScroll id="directory">
            <table className="directory-table">
              <thead>
                <tr>
                  <SortableTh column="name" label="Name" sortKey={sort.sortKey} sortDir={sort.sortDir} onSort={sort.toggle} />
                  <SortableTh column="type" label="Type" sortKey={sort.sortKey} sortDir={sort.sortDir} onSort={sort.toggle} />
                  <SortableTh column="roles" label="Roles" sortKey={sort.sortKey} sortDir={sort.sortDir} onSort={sort.toggle} />
                </tr>
              </thead>
              <tbody>
                {sort.sorted.map((e) => {
                  const href = entryHref(e);
                  const key = `${e.entry_type}-${e.organization_id ?? e.person_id}`;
                  const canDropVcard = canWrite && e.entry_type === 'individual' && e.person_id !== null;
                  return (
                    <tr
                      key={key}
                      className={`${href ? 'directory-row directory-row-link' : 'directory-row'}${dropRow === key ? ' directory-row-dragover' : ''}`}
                      onClick={() => href && navigate(href)}
                      onDragOver={canDropVcard ? (ev) => { ev.preventDefault(); setDropRow(key); } : undefined}
                      onDragLeave={canDropVcard ? () => setDropRow(null) : undefined}
                      onDrop={canDropVcard ? (ev) => onRowDrop(ev, e.person_id!) : undefined}
                    >
                      <td className="directory-name">
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                          {e.entry_type === 'individual' && (
                            <PersonAvatar personId={e.person_id} hasPhoto={e.has_photo} name={e.display_name} size={28} />
                          )}
                          {e.entry_type === 'company' && (
                            <OrgAvatar orgId={e.organization_id} hasLogo={e.has_logo} name={e.display_name} size={28} />
                          )}
                          {e.display_name}
                        </span>
                      </td>
                      <td>
                        <span className={`entity-chip entity-chip-${e.entry_type}`}>
                          {e.entry_type === 'company' ? 'Company' : 'Individual'}
                        </span>
                      </td>
                      <td>
                        <div className="directory-roles">
                          {e.roles.length === 0
                            ? <span className="directory-norole">—</span>
                            : e.roles.map((r) => (
                                <span key={r} className={`role-badge role-${r}`}>{roleLabel(r) || r}</span>
                              ))}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
              </TableScroll>
        </div>
      )}

      <AddCompanyModal open={addCompanyOpen} onClose={() => setAddCompanyOpen(false)} />
      <AddIndividualModal open={addIndividualOpen} onClose={() => setAddIndividualOpen(false)} />
      <VcardImportModal open={importOpen} onClose={() => setImportOpen(false)} />
    </section>
  );
}
